import axios from "axios";

import { clearAuth, getToken } from "utils/auth";

const API_URL =
  import.meta.env.VITE_APP_API_URL?.trim() ||
  "https://deliveryapi.yorotechnologies.com/api";

export const BASE_URL = API_URL;
export const IMG_BASE_URL = API_URL.replace(/\/api$/, "");

/** Build full URL for uploaded images/files returned as relative paths. */
export function resolveAssetUrl(path) {
  if (!path) return null;
  const trimmed = String(path).trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `${IMG_BASE_URL}/${trimmed.replace(/^\//, "")}`;
}

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach saved token on every request so session persists after refresh
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // FormData must be multipart; default application/json turns File fields into {}
  if (config.data instanceof FormData) {
    if (typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else {
      delete config.headers['Content-Type'];
    }
  }

  return config;
});

function shouldForceLogoutOn401(error) {
  if (error.config?.skipAuthLogout) return false;

  const url = String(error.config?.url || "");
  if (url.includes("/login")) return false;

  // Mobile routes may return 401 for role/permission — do not end admin session
  if (url.includes("/mobile/")) return false;

  const data = error.response?.data;
  const msg = String(data?.message || data?.error || "").toLowerCase();

  if (
    msg.includes("permission") ||
    msg.includes("forbidden") ||
    msg.includes("not allowed") ||
    msg.includes("access denied")
  ) {
    return false;
  }

  return true;
}

// Clear session only when the admin token is actually invalid (not on login failures)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401 && getToken() && shouldForceLogoutOn401(error)) {
      clearAuth();
      if (!window.location.pathname.startsWith("/login")) {
        window.location.replace("/login");
      }
    }

    return Promise.reject(error);
  }
);

export default api;